/**
 * L2 drill-down: every form the backend ranked, with its full percentiles.
 *
 * This is where a table belongs (§1). The L1 board shows the worst five and a
 * "View all N →" into here; this page shows the whole ranking the backend
 * produced, which it caps at the fifteen slowest.
 *
 * **No search box, and no per-user view.** Not an omission — the backend
 * deliberately exposes no user identifiers, so there is nothing to search for
 * and no user to filter to. The report's own `TopUsersPain` and
 * `HelpdeskTriage` pages, which ranked named employees by a pain score, have no
 * equivalent anywhere in this feature because no model has a field to put a
 * person in.
 *
 * The `Other` row is the k-anonymity fold, and its `distinct_users`, `p95_ms`
 * and `p99_ms` are all null by design. They render as em dashes, and the
 * caption underneath says how much was folded into it.
 */

import {
  Alert,
  Badge,
  type BreadcrumbItem,
  Breadcrumbs,
  DataGrid,
  EmptyState,
  Freshness,
  PageHeader,
  Skeleton,
  Typography,
} from "@norican/react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { type FoFormPerformance, useFoUserExperience } from "@/lib/api";

import { FoAccessPanel, FoErrorsAlert, isPanelOk } from "./access-panel";
import { DYNAMICS_PATH } from "./params";
import { formatCount, formatMs, formatWindow, suppressionCaption } from "./status";

const BREADCRUMBS: BreadcrumbItem[] = [
  {
    label: "Dynamics",
    render: ({ className, children }: { className: string; children: ReactNode }) => (
      <Link to={DYNAMICS_PATH} className={className}>
        {children}
      </Link>
    ),
  },
  { label: "Forms", current: true },
];

export function DynamicsFormsPage() {
  const experience = useFoUserExperience();
  const data = experience.data;

  return (
    <main className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-8">
      <Breadcrumbs items={BREADCRUMBS} />
      <PageHeader
        title="F&O form performance"
        subtitle="Every form the ranking query returned, slowest by P95 first, then the folded remainder."
        actions={
          data?.collected_at ? (
            <Freshness
              label="Collected"
              since={data.collected_at}
              staleAfter={data.cache_ttl_sec * 2000}
            />
          ) : undefined
        }
      />

      {experience.error && (
        <Alert tone="danger" title="Couldn't load form performance">
          {experience.error instanceof Error
            ? experience.error.message
            : "The NOVA API did not respond. This is a NOVA problem, not an Azure one."}
        </Alert>
      )}

      {experience.isPending && !data ? (
        <Skeleton height="24rem" />
      ) : (
        data &&
        (!isPanelOk(data) ? (
          <FoAccessPanel panel="F&O form performance" state={data} />
        ) : (
          <>
            {data.errors.length > 0 && <FoErrorsAlert errors={data.errors} />}

            <FormsGrid experience={data} />
          </>
        ))
      )}
    </main>
  );
}

interface FormsGridProps {
  experience: NonNullable<ReturnType<typeof useFoUserExperience>["data"]>;
}

function FormsGrid({ experience }: FormsGridProps) {
  const rows = experience.worst_forms;
  const labelled = rows.filter((row) => !row.is_other);

  if (rows.length === 0) {
    return (
      <EmptyState
        title="No form was ranked"
        description="The workspace answered and held no form-load telemetry for this window."
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <Typography variant="body-sm" tone="muted">
        {formatCount(labelled.length)} labelled forms over the {formatWindow(experience.window)},
        judged against a P95 threshold of {formatMs(experience.p95_warning_ms)}. The ranking is
        capped upstream at the slowest forms, so this is a worst-of list rather than a census.
      </Typography>

      <div className="h-[32rem]">
        {/* DataGrid needs a definite height for its sticky header/scroller. */}
        <DataGrid<FoFormPerformance>
          data={rows}
          getRowId={(row) => row.form}
          density="compact"
          aria-label="Every ranked F&O form and its load percentiles"
          columns={[
            {
              id: "form",
              header: "Form",
              accessor: (row) => row.form,
              sortable: true,
              cell: (row) =>
                row.is_other ? (
                  <span className="text-fg-muted inline-flex items-center gap-2 text-sm italic">
                    Other
                    <Badge tone="neutral" variant="outline" size="xs">
                      folded cohort
                    </Badge>
                  </span>
                ) : (
                  <span className="text-fg text-sm font-medium">{row.form}</span>
                ),
            },
            {
              id: "loads",
              header: "Loads",
              align: "end",
              accessor: (row) => row.loads,
              sortable: true,
              cell: (row) => <span className="tabular-nums">{formatCount(row.loads)}</span>,
            },
            {
              id: "users",
              header: "Distinct users",
              align: "end",
              accessor: (row) => row.distinct_users,
              sortable: true,
              cell: (row) => (
                <span className="tabular-nums">{formatCount(row.distinct_users)}</span>
              ),
            },
            {
              id: "avg",
              header: "Mean",
              align: "end",
              accessor: (row) => row.avg_ms,
              sortable: true,
              cell: (row) => <span className="tabular-nums">{formatMs(row.avg_ms)}</span>,
            },
            {
              id: "p95",
              header: "P95",
              align: "end",
              accessor: (row) => row.p95_ms,
              sortable: true,
              cell: (row) => <span className="tabular-nums">{formatMs(row.p95_ms)}</span>,
            },
            {
              id: "p99",
              header: "P99",
              align: "end",
              accessor: (row) => row.p99_ms,
              sortable: true,
              cell: (row) => <span className="tabular-nums">{formatMs(row.p99_ms)}</span>,
            },
            {
              id: "verdict",
              header: "Against threshold",
              accessor: (row) => (row.over_threshold ? "over" : "within"),
              sortable: true,
              cell: (row) =>
                row.p95_ms == null ? (
                  <span className="text-fg-muted text-xs">no percentile</span>
                ) : (
                  <Badge
                    tone={row.over_threshold ? "warning" : "success"}
                    variant="subtle"
                    size="xs"
                  >
                    {row.over_threshold ? "over threshold" : "within threshold"}
                  </Badge>
                ),
            },
          ]}
        />
      </div>

      <Typography variant="body-sm" tone="muted">
        {suppressionCaption(experience.suppression, "loads")}
      </Typography>
    </div>
  );
}
